import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  DollarSign,
  Layers,
  Calendar,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Lock,
  ArrowRight,
  Ban,
  Pause,
  Handshake,
  CheckCircle2,
  Link2,
  AlertTriangle,
  Wrench,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { hasNoUsableClaimedVariations } from "../lib/linkVariations";
import { LinkVariationsModal } from "./LinkVariationsModal";
import { formatCurrency } from "../lib/calculations";
import { useToast } from "./ui/ToastModal";
import type { ClaimsVariation, Json } from "../types/database.types";
import {
  type PaymentLineItem,
  type PaymentAction,
  savePaymentReconciliation,
  pushToNextMonth,
  getActionLabel,
  getActionColor,
} from "../lib/paymentLineItems";
import {
  calculatePaymentReconciliation,
  roundCurrency,
} from "../lib/claimFinancials";
import {
  buildReconciliationSummary,
  getOperationalRetentionPct,
} from "../lib/reconciliationSummary";
import { logActivity } from "../lib/activity-logger";
import type { ReconciliationStatus } from "../lib/reconciliationStatus";
import { calculateDaysBeforeDue } from "../lib/paymentVersions";
import { logError, logWarn } from "../lib/logError";

// ─── Types ────────────────────────────────────────────────────
interface ClaimedSetData {
  generated_at?: string;
  total_inc_gst?: number;
  retention_amount?: number;
  retention_pct?: number;
  progress?: { subcategory_id: string; claimed_percentage: number; claimed_amount: number }[];
  variations?: { variation_id: string; effective_amount: number }[];
  install_hire_this_month?: number;
  hire_billing?: HireBillingClaimedItem[];
}

interface HireBillingClaimedItem {
  hire_billing_id: string;
  hire_register_id?: string | null;
  source_variation_id?: string | null;
  docket_no?: string | null;
  sub_docket_no?: string | null;
  item?: string | null;
  item_description?: string | null;
  weeks_in_period?: number | null;
  amount: number;
  paid?: boolean | null;
  paid_amount?: number | null;
  status?: string | null;
}

interface ClaimsReportRow {
  id: string;
  claim_number: number;
  claim_month: number;
  claim_year: number;
  project_id: string;
  total_inc_gst: number | null;
  total_exc_gst: number | null;
  paid_by_builder: number | null;
  calculated_payment_amount?: number | null;
  difference: number | null;
  date_paid: string | null;
  days_before_due: number | null;
  payment_due_date: string | null;
  variations_this_month?: number | null;
  variations_outstanding?: number | null;
  retention_amount: number | null;
  retention_pct: number | null;
  retention_release_amount?: number | null;
  retention_release_gst?: number | null;
  retention_release_total_inc_gst?: number | null;
  retention_received_amount?: number | null;
  retention_received_notes?: string | null;
  retention_received_at?: string | null;
  progress_payment_pct?: number | null;
  variation_payments?: VariationPayment[] | null;
  claimed_set?: ClaimedSetData | null;
  reconciliation_summary?: unknown;
}

interface VariationPayment {
  variation_id: string;
  paid_pct: number;
  paid_amount: number;
}

interface ProgressLineItem {
  subcategory_id: string;
  subcategory_name: string;
  category_name: string;
  claimed_percentage: number;
  claimed_amount: number;
  budget: number;
  paid_percentage: number;
  paid_amount: number;
  action: PaymentAction;
  agreed_amount: number | null;
  notes: string;
}

interface VariationLineItem {
  variation_id: string;
  variation_name: string;
  claimed_amount: number;
  effective_amount: number;
  paid_pct: number;
  paid_amount: number;
  action: PaymentAction;
  agreed_amount: number | null;
  notes: string;
  /** True when this variation was raised in a prior month and is still outstanding. */
  is_outstanding?: boolean;
}

interface HireLineItem {
  hire_billing_id: string;
  docket_no: string;
  item_name: string;
  weeks_in_period: number;
  claimed_amount: number;
  paid_pct: number;
  paid_amount: number;
  action: PaymentAction;
  agreed_amount: number | null;
  notes: string;
}

interface RetentionLineItem {
  claimed_amount: number;
  paid_amount: number;
  action: PaymentAction;
  agreed_amount: number | null;
  notes: string;
}

// Category/subcategory info passed from parent
interface SubcategoryClaimInfo {
  subcategory_id: string;
  subcategory_name: string;
  category_name: string;
  budget: number;
  claimed_percentage: number;
  claimed_amount: number;
}

export type PaymentFocusSection = "progress" | "variations" | "hire" | "retention" | "all";

interface PaidByBuilderModalProps {
  report: ClaimsReportRow;
  variations: ClaimsVariation[];
  subcategoryClaims: SubcategoryClaimInfo[];
  existingLineItems: PaymentLineItem[];
  retentionAvailableAmount?: number;
  focusSection?: PaymentFocusSection;
  onClose: () => void;
  onSaved: () => void;
  onRefresh?: () => void;
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const ACTION_OPTIONS: { value: PaymentAction; label: string; icon: React.ReactNode }[] = [
  { value: "paid", label: "Paid", icon: <CheckCircle2 size={13} /> },
  { value: "held", label: "Held", icon: <Pause size={13} /> },
  { value: "voided", label: "Voided", icon: <Ban size={13} /> },
  {
    value: "agreement_made",
    label: "Agreement Made",
    icon: <Handshake size={13} />,
  },
  {
    value: "works_not_completed",
    label: "Works Not Completed",
    icon: <ArrowRight size={13} />,
  },
];

function getNextMonth(
  month: number,
  year: number
): { month: number; year: number } {
  return month === 12 ? { month: 1, year: year + 1 } : { month: month + 1, year };
}

function isSyntheticClaimedSetId(id: string | null | undefined): boolean {
  return String(id ?? "").startsWith("__invoiced");
}

function hasMaterialClaimedAmount(amount: number | null | undefined): boolean {
  return Math.abs(Number(amount ?? 0)) > 0.01;
}

/** Shorten long Dashpivot docket numbers: "SEQ Formwork-...-733" → "SEQ-733" */
function shortenDocket(docket: string): string {
  if (!docket) return "Untitled";
  const lastNum = docket.match(/-(\d+)$/)?.[1];
  if (lastNum) return `SEQ-${lastNum}`;
  if (docket.length > 30) return docket.slice(0, 27) + "...";
  return docket;
}

function getHireSourceDocket(row: HireBillingClaimedItem): string {
  return shortenDocket(row.docket_no || row.sub_docket_no || "Install & Hire");
}

// ─── Action Dropdown ──────────────────────────────────────────
function ActionDropdown({
  value,
  onChange,
}: {
  value: PaymentAction;
  onChange: (action: PaymentAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const colors = getActionColor(value);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  const calculateDropdownPosition = () => {
    if (!btnRef.current) return null;
    const rect = btnRef.current.getBoundingClientRect();
    const dropdownWidth = 180;
    const dropdownHeight = 180;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow > dropdownHeight
        ? rect.bottom + 4
        : Math.max(8, rect.top - dropdownHeight - 4);
    const left = Math.min(
      Math.max(8, rect.right - dropdownWidth),
      window.innerWidth - dropdownWidth - 8,
    );

    return { top, left };
  };

  const toggleDropdown = () => {
    if (open) {
      setOpen(false);
      return;
    }

    const nextPosition = calculateDropdownPosition();
    if (!nextPosition) return;
    setDropdownPos(nextPosition);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const nextPosition = calculateDropdownPosition();
      if (nextPosition) setDropdownPos(nextPosition);
    };

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggleDropdown}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 8px",
          borderRadius: 6,
          fontSize: "0.68rem",
          fontWeight: 600,
          border: `1px solid ${colors.border}`,
          background: colors.bg,
          color: colors.text,
          cursor: "pointer",
          transition: "all 0.15s",
          whiteSpace: "nowrap",
        }}
      >
        {ACTION_OPTIONS.find((o) => o.value === value)?.icon}
        {getActionLabel(value)}
        <ChevronDown size={10} />
      </button>
      {open && dropdownPos && createPortal(
        <>
          {/* Backdrop */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 10010 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              background: "#fff",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              zIndex: 10011,
              minWidth: 180,
              overflow: "hidden",
            }}
          >
            {ACTION_OPTIONS.map((opt) => {
              const optColors = getActionColor(opt.value);
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "9px 14px",
                    border: "none",
                    background: isSelected ? optColors.bg : "transparent",
                    color: isSelected ? optColors.text : "#334155",
                    fontSize: "0.78rem",
                    fontWeight: isSelected ? 600 : 500,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "#f8fafc";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span style={{ color: optColors.text }}>{opt.icon}</span>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────
export function PaidByBuilderModal({
  report,
  variations,
  subcategoryClaims,
  existingLineItems,
  retentionAvailableAmount,
  focusSection = "all",
  onClose,
  onSaved,
  onRefresh,
}: PaidByBuilderModalProps) {
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);

  const totalIncGst = Number(report.total_inc_gst ?? 0);

  // Detect if payment was explicitly cleared (no line items + report says $0 paid)
  // In this state, default all amounts to $0 instead of claimed values
  const wasCleared =
    existingLineItems.length === 0 &&
    Number(report.paid_by_builder ?? 0) === 0 &&
    !report.date_paid;

  // Per-type existence checks: when a standalone panel (e.g. Retention) has
  // already saved line items but Progress/Variations were never individually
  // reconciled, those types must default to pending/$0, NOT paid/claimed.
  const hasAnyProgressItems = existingLineItems.some(li => li.line_type === "progress");
  const hasAnyVariationItems = existingLineItems.some(li => li.line_type === "variation");
  const hasAnyHireItems = existingLineItems.some(li => li.line_type === "hire");
  // True first-time open: no line items of ANY kind have ever been saved,
  // AND the payment wasn't explicitly cleared. Only in this case should
  // progress/variation rows auto-fill with paid/claimed amounts.
  const isTrueFirstOpen = existingLineItems.length === 0 && !wasCleared;

  // ─── Section collapse state ─────────────────────────────────
  const [progressOpen, setProgressOpen] = useState(
    focusSection === "all" || focusSection === "progress"
  );
  const [variationsOpen, setVariationsOpen] = useState(
    focusSection === "all" || focusSection === "variations"
  );
  const [hireOpen, setHireOpen] = useState(
    focusSection === "all" || focusSection === "hire"
  );
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [retentionOpen, setRetentionOpen] = useState(
    focusSection === "all" || focusSection === "retention"
  );

  // ─── Legacy detection ───────────────────────────────────────
  const isLegacyReport = hasNoUsableClaimedVariations(report.claimed_set);
  const reportVariationTotal =
    Number(report.variations_this_month ?? 0) +
    Number(report.variations_outstanding ?? 0);
  const currentLinkedIds: string[] = useMemo(() => {
    const vars = report.claimed_set?.variations;
    if (!Array.isArray(vars)) return [];
    return vars
      .filter((v: { variation_id: string }) => !String(v.variation_id ?? "").startsWith("__invoiced"))
      .map((v: { variation_id: string }) => v.variation_id);
  }, [report.claimed_set]);

  // ─── Progress Line Items ────────────────────────────────────
  const [progressLines, setProgressLines] = useState<ProgressLineItem[]>(
    () => {
      // Prefer the frozen claimed set (what the report actually invoiced) so
      // reconciliation always matches the report total. Names/budget are looked
      // up from the live subcategory claims. Synthetic legacy progress snapshots
      // do not map to real subcategories and cannot be saved as line items, so
      // those reports fall back to the live progress rows for the report month.
      const frozenProgress = report.claimed_set?.progress ?? [];
      const realFrozenProgress = frozenProgress.filter(
        fp => !isSyntheticClaimedSetId(fp.subcategory_id)
      );
      const source =
        realFrozenProgress.length > 0
          ? realFrozenProgress.map((fp) => {
              const sc = subcategoryClaims.find(
                (s) => s.subcategory_id === fp.subcategory_id
              );
              return {
                subcategory_id: fp.subcategory_id,
                subcategory_name: sc?.subcategory_name ?? "Unknown",
                category_name: sc?.category_name ?? "Unknown",
                claimed_percentage: fp.claimed_percentage,
                claimed_amount: fp.claimed_amount,
                budget: sc?.budget ?? 0,
              };
            })
          : subcategoryClaims;
      if (source.length === 0) return [];

      return source.map((sc) => {
        // Check if there's an existing line item for this subcategory
        const existing = existingLineItems.find(
          (li) =>
            li.line_type === "progress" &&
            li.subcategory_id === sc.subcategory_id
        );

        if (existing) {
          return {
            subcategory_id: sc.subcategory_id,
            subcategory_name: sc.subcategory_name,
            category_name: sc.category_name,
            claimed_percentage: sc.claimed_percentage,
            claimed_amount: sc.claimed_amount,
            budget: sc.budget,
            paid_percentage: existing.paid_percentage,
            paid_amount: existing.paid_amount,
            action: existing.action,
            agreed_amount: existing.agreed_amount ?? null,
            notes: existing.notes ?? "",
          };
        }

        // Default: only auto-fill paid/claimed on a true first open (no
        // prior standalone panel saves). If other types (e.g. retention) were
        // saved but progress was never reconciled, default to pending/$0.
        const shouldDefaultToPaid = isTrueFirstOpen && !hasAnyProgressItems;
        return {
          subcategory_id: sc.subcategory_id,
          subcategory_name: sc.subcategory_name,
          category_name: sc.category_name,
          claimed_percentage: sc.claimed_percentage,
          claimed_amount: sc.claimed_amount,
          budget: sc.budget,
          paid_percentage: shouldDefaultToPaid ? sc.claimed_percentage : 0,
          paid_amount: shouldDefaultToPaid ? sc.claimed_amount : 0,
          action: (shouldDefaultToPaid ? "paid" : "pending") as PaymentAction,
          agreed_amount: null,
          notes: "",
        };
      });
    }
  );

  // ─── Variation Line Items ───────────────────────────────────
  const [variationLines, setVariationLines] = useState<VariationLineItem[]>(
    () => {
      // Prefer the frozen claimed set: iterate exactly the variations the report
      // invoiced, using their frozen (signed) effective_amount so the Add Payment
      // total matches the report and credits keep their sign. Falls back to the
      // live variations (legacy reports with no claimed_set).
      const frozenVars = Array.isArray(report.claimed_set?.variations)
        ? report.claimed_set.variations
        : null;
      // Filter out synthetic legacy IDs (backfilled "__invoiced_*"), because they don't
      // map to real variation rows. Also ignore zero legacy rows produced after
      // variations were already paid; a frozen snapshot means "use only known
      // report rows", not "fall back to every currently outstanding variation".
      const realFrozenVars = (frozenVars ?? []).filter(
        fv =>
          !isSyntheticClaimedSetId(fv.variation_id) &&
          hasMaterialClaimedAmount(fv.effective_amount)
      );
      const sources: { v: ClaimsVariation; frozenAmount: number | null }[] =
        frozenVars
          ? realFrozenVars.map((fv) => {
              const match = variations.find((x) => x.id === fv.variation_id);
              return {
                v:
                  match ??
                  ({
                    id: fv.variation_id,
                    docket_no: fv.variation_id,
                    total: fv.effective_amount,
                    discount_percent: 0,
                    approved_amount: 0,
                  } as unknown as ClaimsVariation),
                frozenAmount: fv.effective_amount,
              };
            })
          : variations.map((v) => ({ v, frozenAmount: null }));

      return sources.map(({ v, frozenAmount }) => {
        const total = Number(v.total ?? 0);
        const discount = Number(v.discount_percent ?? 0);
        const effective =
          frozenAmount != null
            ? frozenAmount
            : discount > 0
              ? total * (1 - discount / 100)
              : total;
        const approved = Number(v.approved_amount ?? 0);
        const pct =
          effective > 0
            ? Math.round((approved / effective) * 100 * 100) / 100
            : 0;

        const vDisplayName = isSyntheticClaimedSetId(v.id)
          ? "Variations (as invoiced)"
          : shortenDocket(v.docket_no);

        // Outstanding = raised in a prior month relative to this report.
        const vDate = v.variation_date ? new Date(v.variation_date) : null;
        const isOutstanding =
          !!vDate &&
          (vDate.getFullYear() < report.claim_year ||
            (vDate.getFullYear() === report.claim_year &&
              vDate.getMonth() + 1 < report.claim_month));

        // Check for existing line item
        const existing = existingLineItems.find(
          (li) =>
            li.line_type === "variation" && li.variation_id === v.id
        );
        const legacyPayment = report.variation_payments?.find(
          (payment) => payment.variation_id === v.id
        );

        if (existing) {
          return {
            variation_id: v.id,
            variation_name: vDisplayName,
            claimed_amount: effective,
            effective_amount: effective,
            is_outstanding: isOutstanding,
            paid_pct: existing.paid_percentage,
            paid_amount: existing.paid_amount,
            action: existing.action,
            agreed_amount: existing.agreed_amount ?? null,
            notes: existing.notes ?? "",
          };
        }

        if (legacyPayment) {
          return {
            variation_id: v.id,
            variation_name: vDisplayName,
            claimed_amount: effective,
            effective_amount: effective,
            is_outstanding: isOutstanding,
            paid_pct: Number(legacyPayment.paid_pct ?? 0),
            paid_amount: Number(legacyPayment.paid_amount ?? 0),
            action: Number(legacyPayment.paid_amount ?? 0) > 0 ? "paid" : "pending",
            agreed_amount: null,
            notes: "",
          };
        }

        // Pre-fill from variation's approved_amount if no line item exists
        // so totals match what was actually paid
        if (approved > 0) {
          return {
            variation_id: v.id,
            variation_name: vDisplayName,
            claimed_amount: effective,
            effective_amount: effective,
            is_outstanding: isOutstanding,
            paid_pct: pct,
            paid_amount: approved,
            action: "paid" as PaymentAction,
            agreed_amount: null,
            notes: "",
          };
        }

        // Only auto-fill paid/claimed on true first open with no prior saves.
        const shouldDefaultToPaid = isTrueFirstOpen && !hasAnyVariationItems;
        return {
          variation_id: v.id,
          variation_name: vDisplayName,
          claimed_amount: effective,
          effective_amount: effective,
          paid_pct: shouldDefaultToPaid ? pct : 0,
          paid_amount: shouldDefaultToPaid ? Math.round(effective * (pct / 100) * 100) / 100 : 0,
          action: (shouldDefaultToPaid ? "paid" : "pending") as PaymentAction,
          agreed_amount: null,
          notes: "",
        };
      });
    }
  );

  // ─── Install & Hire Billing Line Items ─────────────────────
  const [hireLines, setHireLines] = useState<HireLineItem[]>(
    () => {
      const frozenHireRows = Array.isArray(report.claimed_set?.hire_billing)
        ? report.claimed_set.hire_billing.filter((row) =>
            typeof row.hire_billing_id === "string" &&
            hasMaterialClaimedAmount(row.amount)
          )
        : [];

      return frozenHireRows.map((row) => {
        const claimedAmount = roundCurrency(Number(row.amount || 0));
        const existing = existingLineItems.find(
          (li) =>
            li.line_type === "hire" &&
            li.hire_billing_id === row.hire_billing_id
        );

        if (existing) {
          return {
            hire_billing_id: row.hire_billing_id,
            docket_no: getHireSourceDocket(row),
            item_name: row.item_description || row.item || "Hire item",
            weeks_in_period: Number(row.weeks_in_period || 0),
            claimed_amount: claimedAmount,
            paid_pct: existing.paid_percentage,
            paid_amount: existing.paid_amount,
            action: existing.action,
            agreed_amount: existing.agreed_amount ?? null,
            notes: existing.notes ?? "",
          };
        }

        if (row.paid && Number(row.paid_amount || 0) > 0) {
          const paidAmount = roundCurrency(Number(row.paid_amount || 0));
          return {
            hire_billing_id: row.hire_billing_id,
            docket_no: getHireSourceDocket(row),
            item_name: row.item_description || row.item || "Hire item",
            weeks_in_period: Number(row.weeks_in_period || 0),
            claimed_amount: claimedAmount,
            paid_pct: claimedAmount !== 0 ? roundCurrency((paidAmount / claimedAmount) * 100) : 0,
            paid_amount: paidAmount,
            action: "paid" as PaymentAction,
            agreed_amount: null,
            notes: "",
          };
        }

        const shouldDefaultToPaid = isTrueFirstOpen && !hasAnyHireItems;
        return {
          hire_billing_id: row.hire_billing_id,
          docket_no: getHireSourceDocket(row),
          item_name: row.item_description || row.item || "Hire item",
          weeks_in_period: Number(row.weeks_in_period || 0),
          claimed_amount: claimedAmount,
          paid_pct: shouldDefaultToPaid ? 100 : 0,
          paid_amount: shouldDefaultToPaid ? claimedAmount : 0,
          action: (shouldDefaultToPaid ? "paid" : "pending") as PaymentAction,
          agreed_amount: null,
          notes: "",
        };
      });
    }
  );

  // ─── Retention Line Item ────────────────────────────────────
  const retentionClaimed = roundCurrency(
    Math.max(
      0,
      retentionAvailableAmount ?? Number(report.retention_release_amount ?? 0),
    ),
  );
  const currentReportRelease = roundCurrency(Number(report.retention_release_amount ?? 0));
  const [retentionLine, setRetentionLine] = useState<RetentionLineItem>(
    () => {
      const existing = existingLineItems.find(
        (li) => li.line_type === "retention"
      );
      if (existing) {
        return {
          claimed_amount: retentionClaimed,
          paid_amount: existing.paid_amount,
          action: existing.action,
          agreed_amount: existing.agreed_amount ?? null,
          notes: existing.notes ?? "",
        };
      }
      // Single source of truth: retention released may have been recorded via the
      // dedicated Retention panel / bulk distribute, which writes
      // claims_reports.retention_received_amount even when no line item is loaded
      // here. Reflect that so Add Payment never shows 0 for retention already paid.
      const receivedElsewhere = roundCurrency(
        Number(report.retention_received_amount ?? 0)
      );
      if (receivedElsewhere > 0) {
        return {
          claimed_amount: retentionClaimed,
          paid_amount: receivedElsewhere,
          action: "paid" as PaymentAction,
          agreed_amount: null,
          notes: report.retention_received_notes ?? "",
        };
      }
      return {
        claimed_amount: retentionClaimed,
        paid_amount: 0,
        action: "pending" as PaymentAction,
        agreed_amount: null,
        notes: "",
      };
    }
  );

  // ─── Retention Held (in Payment Summary) ────────────────────
  // This represents the amount the builder WITHHOLDS from the current payment.
  // Distinct from the Retention section above, which is for RELEASING retention back.
  const [retentionHeldEnabled, setRetentionHeldEnabled] = useState<boolean>(
    () => getOperationalRetentionPct(report) > 0
  );
  const [retentionHeldPct, setRetentionHeldPct] = useState<number>(
    () => getOperationalRetentionPct(report) || 5
  );

  // Date paid
  const [datePaid, setDatePaid] = useState(report.date_paid ?? "");

  // ─── Calculations ──────────────────────────────────────────
  const variationsPaidTotal = useMemo(
    () =>
      variationLines.reduce(
        (sum, vl) =>
          sum +
          (vl.action === "paid" || vl.action === "agreement_made" ? vl.paid_amount : 0),
        0
      ),
    [variationLines]
  );

  const hirePaidTotal = useMemo(
    () =>
      roundCurrency(
        hireLines.reduce(
          (sum, hl) =>
            sum +
            (hl.action === "paid" || hl.action === "agreement_made" ? hl.paid_amount : 0),
          0
        )
      ),
    [hireLines]
  );

  const retentionPaidTotal = useMemo(() => {
    if (retentionLine.action !== "paid") return 0;
    return roundCurrency(Math.min(Math.max(0, retentionLine.paid_amount), retentionClaimed));
  }, [retentionLine, retentionClaimed]);

  // Sum of actual progress line item paid amounts (not slider-based)
  const progressLinesPaidTotal = useMemo(
    () =>
      roundCurrency(
        progressLines.reduce(
          (sum, pl) =>
            sum +
            (pl.action === "paid" || pl.action === "agreement_made" ? pl.paid_amount : 0),
          0
        )
      ),
    [progressLines]
  );

  const [amountReceivedInput, setAmountReceivedInput] = useState<string>(() => {
    const existingReceived = roundCurrency(Number(report.paid_by_builder ?? 0));
    return existingReceived > 0 ? String(existingReceived) : "";
  });

  // Amount Received intentionally defaults to empty ($0) so the user must
  // manually enter the actual amount received in the bank. No auto-fill.

  const amountReceived = useMemo(
    () => roundCurrency(Number(amountReceivedInput || 0)),
    [amountReceivedInput]
  );

  const paymentSummary = useMemo(
    () =>
      calculatePaymentReconciliation({
        progressPaid: progressLinesPaidTotal,
        variationsPaid: variationsPaidTotal + hirePaidTotal,
        retentionPct: retentionHeldPct,
        retentionHeldEnabled,
        retentionReleased: retentionPaidTotal,
        amountReceived,
      }),
    [
      progressLinesPaidTotal,
      variationsPaidTotal,
      hirePaidTotal,
      retentionHeldPct,
      retentionHeldEnabled,
      retentionPaidTotal,
      amountReceived,
    ]
  );

  const subtotalExcGst = paymentSummary.subtotalExcGst;
  const gstAmount = paymentSummary.gstAmount;
  const retentionHeldAmount = paymentSummary.retentionHeldAmount;
  const taxableSubtotalExcGst = roundCurrency(
    Math.max(0, subtotalExcGst - retentionHeldAmount) +
      Math.max(0, retentionPaidTotal),
  );
  const subtotalIncGst = paymentSummary.subtotalIncGst;
  const calculatedPaymentAmount = paymentSummary.calculatedPaymentAmount;
  const totalPaidByBuilder = paymentSummary.amountReceived;
  const difference = paymentSummary.difference;

  // ─── Dirty state tracking ─────────────────────────────────
  const initialDatePaid = useRef(datePaid);
  const initialProgressLines = useRef(JSON.stringify(progressLines));
  const initialVariationLines = useRef(JSON.stringify(variationLines));
  const initialHireLines = useRef(JSON.stringify(hireLines));
  const initialRetentionLine = useRef(JSON.stringify(retentionLine));
  const initialRetentionHeldEnabled = useRef(retentionHeldEnabled);
  const initialRetentionHeldPct = useRef(retentionHeldPct);
  const initialAmountReceivedInput = useRef(amountReceivedInput);

  const isDirty = useMemo(() => {
    if (datePaid !== initialDatePaid.current) return true;
    if (JSON.stringify(progressLines) !== initialProgressLines.current)
      return true;
    if (JSON.stringify(variationLines) !== initialVariationLines.current)
      return true;
    if (JSON.stringify(hireLines) !== initialHireLines.current)
      return true;
    if (JSON.stringify(retentionLine) !== initialRetentionLine.current)
      return true;
    if (retentionHeldEnabled !== initialRetentionHeldEnabled.current) return true;
    if (retentionHeldPct !== initialRetentionHeldPct.current) return true;
    if (amountReceivedInput !== initialAmountReceivedInput.current) return true;
    return false;
  }, [datePaid, progressLines, variationLines, hireLines, retentionLine, retentionHeldEnabled, retentionHeldPct, amountReceivedInput]);


  // ─── Progress Line Action Handlers ─────────────────────────
  const updateProgressLine = useCallback(
    (idx: number, updates: Partial<ProgressLineItem>) => {
      setProgressLines((prev) =>
        prev.map((line, i) => {
          if (i !== idx) return line;
          const updated = { ...line, ...updates };

          // Auto-adjust values based on action
          if (updates.action === "voided" || updates.action === "held") {
            updated.paid_percentage = 0;
            updated.paid_amount = 0;
          } else if (updates.action === "paid" && !updates.paid_percentage) {
            // Auto-fill only on fresh selection (allows overpayment)
            if (line.paid_amount === 0 || line.action === "voided" || line.action === "held" || line.action === "works_not_completed" || line.action === "pending") {
              updated.paid_percentage = line.claimed_percentage;
              updated.paid_amount = line.claimed_amount;
            }
          }

          return updated;
        })
      );
    },
    []
  );

  // ─── Variation Line Action Handlers ────────────────────────
  const updateVariationLine = useCallback(
    (idx: number, updates: Partial<VariationLineItem>) => {
      setVariationLines((prev) =>
        prev.map((line, i) => {
          if (i !== idx) return line;
          const updated = { ...line, ...updates };

          if (updates.action === "voided" || updates.action === "held") {
            updated.paid_pct = 0;
            updated.paid_amount = 0;
          } else if (updates.action === "paid" && !updates.paid_amount) {
            // Auto-fill only on fresh selection (allows overpayment)
            if (line.paid_amount === 0 || line.action === "voided" || line.action === "held" || line.action === "works_not_completed" || line.action === "pending") {
              updated.paid_pct = 100;
              updated.paid_amount = line.effective_amount;
            }
          }

          return updated;
        })
      );
    },
    []
  );

  // ─── Install & Hire Line Action Handlers ────────────────────
  const updateHireLine = useCallback(
    (idx: number, updates: Partial<HireLineItem>) => {
      setHireLines((prev) =>
        prev.map((line, i) => {
          if (i !== idx) return line;
          const updated = { ...line, ...updates };

          if (updates.action === "voided" || updates.action === "held" || updates.action === "works_not_completed" || updates.action === "pending") {
            updated.paid_pct = 0;
            updated.paid_amount = 0;
          } else if (updates.action === "paid" && !updates.paid_amount) {
            if (line.paid_amount === 0 || line.action === "voided" || line.action === "held" || line.action === "works_not_completed" || line.action === "pending") {
              updated.paid_pct = 100;
              updated.paid_amount = line.claimed_amount;
            }
          }

          return updated;
        })
      );
    },
    []
  );

  // ─── Save Handler ──────────────────────────────────────────
  const handleSave = async () => {
    if (!isDirty) {
      addToast({ type: "info", title: "No changes to save" });
      return;
    }



    setSaving(true);
    try {
      const daysBeforeDue = calculateDaysBeforeDue(datePaid, report.payment_due_date);

      // 1. Build payment line items for the new table
      const lineItems: PaymentLineItem[] = [];
      const isPayingAction = (action: PaymentAction) =>
        action === "paid" || action === "agreement_made";

      // Progress lines (only subcategories with claims)
      for (const pl of progressLines) {
        // Lumped legacy lines (backfilled claimed_set) carry synthetic ids that
        // are not real UUIDs, so never persist them as line items (the RPC would
        // fail the uuid cast). The report-level totals still save via the RPC args.
        if (isSyntheticClaimedSetId(pl.subcategory_id)) continue;
        if (pl.claimed_percentage <= 0 && pl.action === "paid") continue;
        const paymentCounts = isPayingAction(pl.action);
        lineItems.push({
          report_id: report.id,
          line_type: "progress",
          subcategory_id: pl.subcategory_id,
          claimed_percentage: pl.claimed_percentage,
          claimed_amount: pl.claimed_amount,
          paid_percentage:
            paymentCounts ? pl.paid_percentage : 0,
          paid_amount: paymentCounts ? pl.paid_amount : 0,
          action: pl.action,
          agreed_amount:
            pl.action === "agreement_made"
              ? pl.paid_amount
              : null,
          notes: pl.notes || null,
          pushed_to_month:
            pl.action === "works_not_completed"
              ? getNextMonth(report.claim_month, report.claim_year)
                  .month
              : null,
          pushed_to_year:
            pl.action === "works_not_completed"
              ? getNextMonth(report.claim_month, report.claim_year)
                  .year
              : null,
        });
      }

      // Variation lines
      for (const vl of variationLines) {
        // Skip lumped legacy synthetic ids (see progress note above).
        if (isSyntheticClaimedSetId(vl.variation_id)) continue;
        const paymentCounts = isPayingAction(vl.action);
        lineItems.push({
          report_id: report.id,
          line_type: "variation",
          variation_id: vl.variation_id,
          claimed_percentage: 100,
          claimed_amount: vl.claimed_amount,
          paid_percentage: paymentCounts ? vl.paid_pct : 0,
          paid_amount: paymentCounts ? vl.paid_amount : 0,
          action: vl.action,
          agreed_amount:
            vl.action === "agreement_made"
              ? vl.paid_amount
              : null,
          notes: vl.notes || null,
        });
      }

      // Install & Hire billing lines. These are separate from the parent
      // variation and sync back to claims_hire_billing through the payment RPC.
      for (const hl of hireLines) {
        const paymentCounts = isPayingAction(hl.action);
        lineItems.push({
          report_id: report.id,
          line_type: "hire",
          hire_billing_id: hl.hire_billing_id,
          claimed_percentage: 100,
          claimed_amount: hl.claimed_amount,
          paid_percentage: paymentCounts ? hl.paid_pct : 0,
          paid_amount: paymentCounts ? hl.paid_amount : 0,
          action: hl.action,
          agreed_amount:
            hl.action === "agreement_made"
              ? hl.paid_amount
              : null,
          notes: hl.notes || null,
        });
      }

      // Retention line
      if (retentionClaimed > 0) {
        const retentionReleased = retentionLine.action === "paid";
        const retentionLinePaidAmount = retentionReleased
          ? roundCurrency(Math.min(Math.max(0, retentionLine.paid_amount), retentionClaimed))
          : 0;
        lineItems.push({
          report_id: report.id,
          line_type: "retention",
          claimed_percentage: 100,
          claimed_amount: retentionClaimed,
          paid_percentage:
            retentionReleased && retentionLinePaidAmount > 0
                ? Math.round(
                    (retentionLinePaidAmount / retentionClaimed) *
                      100 *
                      100
                  ) / 100
                : 0,
          paid_amount: retentionLinePaidAmount,
          action: retentionLine.action,
          agreed_amount: null,
          notes: retentionLine.notes || null,
        });
      }

      // 2. Handle "Works Not Completed": push to next month
      for (const pl of progressLines) {
        if (isSyntheticClaimedSetId(pl.subcategory_id)) continue;
        if (pl.action === "works_not_completed" && pl.claimed_percentage > 0) {
          await pushToNextMonth(
            pl.subcategory_id,
            pl.claimed_percentage,
            pl.claimed_amount,
            report.claim_month,
            report.claim_year
          );
        }
      }

      const nextVariationPayments = variationLines
        .filter((vl) => !isSyntheticClaimedSetId(vl.variation_id))
        .map((vl) => {
          const paymentCounts = isPayingAction(vl.action);
          return {
            variation_id: vl.variation_id,
            paid_pct: paymentCounts ? vl.paid_pct : 0,
            paid_amount: paymentCounts ? vl.paid_amount : 0,
            action: vl.action,
          };
        });

      const retentionReceivedAmount =
        retentionPaidTotal > 0 ? retentionPaidTotal : null;
      const retentionReceivedAt =
        retentionPaidTotal > 0
          ? report.retention_received_at || new Date().toISOString()
          : null;
      const retentionReceivedNotes =
        retentionPaidTotal > 0 ? retentionLine.notes?.trim() || null : null;
      const hasPaymentEvent = lineItems.length > 0;
      const effectiveDatePaid = hasPaymentEvent ? (datePaid || null) : null;
      const effectiveDaysBeforeDue = hasPaymentEvent ? daysBeforeDue : null;
      // Only create a version history entry when MODIFYING an existing payment.
      // Initial "Add Payment" should not generate history, because it is the baseline.
      const isModification = !isTrueFirstOpen;
      const versionSummary = isModification
        ? `Payment reconciliation: received ${formatCurrency(totalPaidByBuilder)} ` +
          `vs ${formatCurrency(calculatedPaymentAmount)} expected; ` +
          `retention held ${retentionHeldEnabled ? `${retentionHeldPct}%` : "off"}` +
          `${hirePaidTotal > 0 ? `, install & hire ${formatCurrency(hirePaidTotal)}` : ""}` +
          `${retentionPaidTotal > 0 ? `, released ${formatCurrency(retentionPaidTotal)}` : ""}.`
        : undefined;

      const { success, error: saveError } = await savePaymentReconciliation({
        reportId: report.id,
        lineItems,
        progressPaymentPct: paymentSummary.progressPaymentPct,
        variationPayments: nextVariationPayments as unknown as Json,
        paidByBuilder: totalPaidByBuilder,
        calculatedPaymentAmount,
        difference,
        datePaid: effectiveDatePaid,
        daysBeforeDue: effectiveDaysBeforeDue,
        hasUnresolvedItems: lineItems.some((li) => li.action !== "paid"),
        retentionPct: retentionHeldEnabled ? retentionHeldPct : 0,
        retentionAmount: retentionHeldAmount,
        retentionReceivedAmount,
        retentionReceivedNotes,
        retentionReceivedAt,
        reconciliationSummary: buildReconciliationSummary({
          progressPaidExGst: progressLinesPaidTotal,
          variationsPaidExGst: variationsPaidTotal,
          hirePaidExGst: hirePaidTotal,
          retentionPct: retentionHeldPct,
          retentionHeldEnabled,
          retentionReleasedExGst: retentionPaidTotal,
          amountReceivedIncGst: totalPaidByBuilder,
        }),
        changesSummary: versionSummary,
      });

      if (!success) throw new Error(saveError || "Could not save payment reconciliation");

      // 3. Sync only CHANGED variations to claims_variations + Dashpivot
      const currentUser = (await supabase.auth.getUser()).data.user;

      for (const vp of nextVariationPayments) {
        const variation = variations.find(
          (v) => v.id === vp.variation_id
        );
        if (!variation) continue;

        // Map payment action to reconciliation status:
        // only 'paid' → 'paid'; everything else → null
        const reconStatus: ReconciliationStatus = vp.action === 'paid' ? 'paid' : null;

        const currentApproved = Number(variation.approved_amount ?? 0);

        // If amounts haven't changed, still update reconciliation if action is 'paid'
        if (Math.abs(vp.paid_amount - currentApproved) < 0.01) {
          if (reconStatus !== null) {
            console.log(`[PBB] Amounts unchanged, updating reconciliation → ${reconStatus}`);
            await supabase
              .from("claims_variations")
              .update({
                reconciliation_status: reconStatus,
                reconciled_by: currentUser?.id ?? null,
                reconciled_at: new Date().toISOString(),
              })
              .eq("id", vp.variation_id);
          }
          continue;
        }

        const total = Number(variation.total ?? 0);
        const discount = Number(variation.discount_percent ?? 0);
        const effective =
          discount > 0 ? total * (1 - discount / 100) : total;
        const newApproved = Math.min(vp.paid_amount, effective);
        const newWaiting =
          Math.max(
            0,
            Math.round((effective - newApproved) * 100) / 100
          );

        await supabase
          .from("claims_variations")
          .update({
            approved_amount: newApproved,
            waiting_amount: newWaiting,
            ...(reconStatus !== null && {
              reconciliation_status: reconStatus,
              reconciled_by: currentUser?.id ?? null,
              reconciled_at: new Date().toISOString(),
            }),
          })
          .eq("id", vp.variation_id);

        // Push to Dashpivot if form is linked
        if (variation.dashpivot_form_id) {
          try {
            const { error: pushError } = await supabase.functions.invoke(
              "push-variation-update",
              {
                body: {
                  dashpivotFormId: variation.dashpivot_form_id,
                  approvedAmount: Math.round(newApproved * 100) / 100,
                  projectId: report.project_id,
                },
              },
            );
            if (pushError) {
              logWarn(`Dashpivot push failed for a variation:`, pushError);
            }
          } catch (pushErr) {
            logWarn(`Dashpivot push error for a variation:`, pushErr);
          }
        }
      }

      // 7. Activity logging: log each non-default action
      const period = `${MONTH_SHORT[report.claim_month - 1]} ${report.claim_year}`;
      const actionSummaries: string[] = [];

      for (const pl of progressLines) {
        if (pl.action !== "paid") {
          actionSummaries.push(
            `${pl.category_name} > ${pl.subcategory_name}: ${getActionLabel(pl.action)}`
          );
        }
      }
      for (const vl of variationLines) {
        if (vl.action !== "paid") {
          actionSummaries.push(
            `${vl.variation_name}: ${getActionLabel(vl.action)}`
          );
        }
      }
      for (const hl of hireLines) {
        if (hl.action !== "paid") {
          actionSummaries.push(
            `${hl.docket_no} ${hl.item_name}: ${getActionLabel(hl.action)}`
          );
        }
      }
      if (
        retentionClaimed > 0 &&
        retentionLine.action !== "paid" &&
        retentionLine.action !== "pending"
      ) {
        actionSummaries.push(
          `Retention: ${getActionLabel(retentionLine.action)}`
        );
      }

      // Always log the payment update
      await logActivity({
        projectId: report.project_id,
        actionType: "UPDATE",
        entityType: "PAYMENT",
        entityId: report.id,
        description: actionSummaries.length > 0
          ? `Payment reconciled for Claim #${report.claim_number} (${period}): ${formatCurrency(totalPaidByBuilder)} received against ${formatCurrency(calculatedPaymentAmount)} expected. Actions: ${actionSummaries.join("; ")}`
          : `Payment updated for Claim #${report.claim_number} (${period}): ${formatCurrency(totalPaidByBuilder)} received against ${formatCurrency(calculatedPaymentAmount)} expected`,
        metadata: {
          claim_number: report.claim_number,
          claim_month: report.claim_month,
          claim_year: report.claim_year,
          total_claimed: totalIncGst,
          calculated_payment_amount: calculatedPaymentAmount,
          amount_received: totalPaidByBuilder,
          difference,
          progress_pct: paymentSummary.progressPaymentPct,
          date_paid: datePaid,
          actions: lineItems
            .filter((li) => li.action !== "paid")
            .map((li) => ({
              type: li.line_type,
              action: li.action,
              claimed: li.claimed_amount,
              paid: li.paid_amount,
            })),
        },
      });

      addToast({
        type: "success",
        title: "Payment Updated",
        message: `Received: ${formatCurrency(totalPaidByBuilder)}. Difference: ${formatCurrency(difference)}`,
      });
      onSaved();
    } catch (err) {
      logError("Error saving payment:", err);
      addToast({
        type: "error",
        title: "Save Failed",
        message:
          err instanceof Error ? err.message : "Could not save payment",
      });
    } finally {
      setSaving(false);
    }
  };

  const period = `${MONTH_SHORT[report.claim_month - 1]} ${report.claim_year}`;
  const nextPeriod = getNextMonth(report.claim_month, report.claim_year);
  const nextPeriodLabel = `${MONTH_SHORT[nextPeriod.month - 1]} ${nextPeriod.year}`;

  // Count non-paid actions for badge
  const actionCount = [
    ...progressLines.filter((pl) => pl.action !== "paid"),
    ...variationLines.filter((vl) => vl.action !== "paid"),
    ...hireLines.filter((hl) => hl.action !== "paid"),
    ...(retentionLine.action !== "paid" && retentionLine.action !== "pending" ? [retentionLine] : []),
  ].length;

  return (
    <>
      <div
        className="payment-reconciliation-overlay"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
          padding: 20,
        }}
        onClick={onClose}
      >
        <div
          className="payment-reconciliation-modal"
          style={{
            background: "#fff",
            borderRadius: 20,
            width: "100%",
            maxWidth: 560,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            boxShadow:
              "0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(59,130,246,0.06)",
            overflow: "hidden",
          }}
          onClick={(e) => e.stopPropagation()}
        >


          {/* Header */}
          <div
            className="payment-reconciliation-header"
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background:
                    "linear-gradient(135deg, #3b82f6, #2563eb)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(59,130,246,0.3)",
                }}
              >
                <DollarSign size={18} color="#fff" />
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#0f172a",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  Payment Reconciliation
                  {focusSection !== "all" && (
                    <span
                      style={{
                        background:
                          focusSection === "progress" ? "#f0fdf4"
                            : focusSection === "variations" ? "#f5f3ff"
                              : focusSection === "hire" ? "#f0f9ff"
                              : "#fffbeb",
                        color:
                          focusSection === "progress" ? "#16a34a"
                            : focusSection === "variations" ? "#7c3aed"
                              : focusSection === "hire" ? "#0369a1"
                              : "#d97706",
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        padding: "1px 8px",
                        borderRadius: 8,
                        textTransform: "capitalize",
                      }}
                    >
                      {focusSection === "progress" ? "Progress"
                        : focusSection === "variations" ? "Variations"
                          : focusSection === "hire" ? "Install & Hire"
                          : "Retention"}
                    </span>
                  )}
                  {actionCount > 0 && (
                    <span
                      style={{
                        background: "#fef3c7",
                        color: "#d97706",
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        padding: "1px 6px",
                        borderRadius: 8,
                      }}
                    >
                      {actionCount} action{actionCount !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: "0.72rem",
                    color: "#64748b",
                  }}
                >
                  Claim #{report.claim_number} • {period}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#64748b",
                padding: 6,
                borderRadius: 8,
                transition: "all 0.15s",
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Scrollable Body */}
          <div
            className="payment-reconciliation-body"
            style={{
              overflow: "auto",
              flex: 1,
              padding: "20px 24px",
            }}
          >
            {/* ── Section 1: Progress Claim Breakdown ── */}
            {progressLines.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <button
                  type="button"
                  onClick={() => setProgressOpen(!progressOpen)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "8px 0",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <TrendingUp size={14} color="#16a34a" />
                    <span
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Progress Claim Breakdown
                    </span>
                    <span
                      style={{
                        fontSize: "0.62rem",
                        fontWeight: 500,
                        color: "#64748b",
                        background: "#f1f5f9",
                        padding: "1px 6px",
                        borderRadius: 4,
                      }}
                    >
                      {progressLines.filter((pl) => pl.claimed_percentage > 0).length} items
                    </span>
                  </div>
                  {progressOpen ? (
                    <ChevronUp size={16} color="#64748b" />
                  ) : (
                    <ChevronDown size={16} color="#64748b" />
                  )}
                </button>

                {progressOpen && (
                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      overflow: "hidden",
                    }}
                  >
                    {/* Table header */}
                    <div
                      className="payment-progress-grid-header"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 80px 90px 140px",
                        gap: 4,
                        padding: "8px 12px",
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      <span>Category / Subcategory</span>
                      <span style={{ textAlign: "right" }}>Claimed</span>
                      <span style={{ textAlign: "right" }}>Amount Paid</span>
                      <span style={{ textAlign: "right" }}>Action</span>
                    </div>

                    {/* Line items */}
                    {progressLines
                      .filter(
                        (pl) => pl.claimed_percentage > 0
                      )
                      .map((pl, idx) => {
                        const actualIdx = progressLines.indexOf(pl);
                        const actionColors = getActionColor(pl.action);
                        return (
                          <div
                            key={pl.subcategory_id}
                            className="payment-progress-grid-row"
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "1fr 80px 90px 140px",
                              gap: 4,
                              padding: "8px 12px",
                              borderBottom:
                                idx <
                                progressLines.filter(
                                  (p) =>
                                    p.claimed_percentage > 0
                                ).length -
                                  1
                                  ? "1px solid #f1f5f9"
                                  : "none",
                              fontSize: "0.75rem",
                              alignItems: "center",
                              background:
                                pl.action !== "paid"
                                  ? actionColors.bg
                                  : "transparent",
                              transition: "background 0.15s",
                            }}
                          >
                            {/* Name */}
                            <div className="payment-progress-name">
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: "#0f172a",
                                  fontSize: "0.72rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 140,
                                }}
                                title={`${pl.category_name} > ${pl.subcategory_name}`}
                              >
                                {pl.subcategory_name}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.62rem",
                                  color: "#94a3b8",
                                }}
                              >
                                {pl.category_name}
                              </div>
                            </div>

                            {/* Claimed */}
                            <div
                              style={{
                                textAlign: "right",
                                fontWeight: 500,
                                color: "#64748b",
                              }}
                            >
                              {pl.claimed_percentage}%
                              <div data-seq-private=""
                                style={{
                                  fontSize: "0.6rem",
                                  color: "#94a3b8",
                                }}
                              >
                                {formatCurrency(
                                  pl.claimed_amount
                                )}
                              </div>
                            </div>

                            {/* Amount paid: editable, with percentage derived for grid/history */}
                            <div
                              className="payment-progress-amount"
                              data-label="Amount Paid"
                              style={{ textAlign: "right" }}
                            >
                              <input data-seq-private
                                aria-label={`Amount paid for ${pl.subcategory_name}`}
                                type="number"
                                value={
                                  pl.action === "voided" ||
                                  pl.action === "held" ||
                                  pl.action === "works_not_completed" ||
                                  pl.action === "pending"
                                    ? 0
                                    : pl.paid_amount
                                }
                                disabled={
                                  pl.action === "voided" ||
                                  pl.action === "held" ||
                                  pl.action === "works_not_completed" ||
                                  pl.action === "pending"
                                }
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const amt = raw === "" ? 0 : parseFloat(raw);
                                  if (isNaN(amt)) return;
                                  updateProgressLine(actualIdx, {
                                    paid_amount: amt,
                                    paid_percentage:
                                      pl.budget > 0
                                        ? Math.round((amt / pl.budget) * 100 * 100) / 100
                                        : 0,
                                  });
                                }}
                                style={{
                                  width: "100%",
                                  padding: "4px 4px",
                                  border: `1px solid ${
                                    pl.action === "voided" ||
                                    pl.action === "held" ||
                                    pl.action === "works_not_completed" ||
                                    pl.action === "pending"
                                      ? "#e2e8f0"
                                      : "#d1d5db"
                                  }`,
                                  borderRadius: 5,
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  textAlign: "right",
                                  outline: "none",
                                  background:
                                    pl.action === "voided" ||
                                    pl.action === "held" ||
                                    pl.action === "works_not_completed" ||
                                    pl.action === "pending"
                                      ? "#f1f5f9"
                                      : "#fff",
                                  color:
                                    pl.action === "voided" ||
                                    pl.action === "held" ||
                                    pl.action === "works_not_completed" ||
                                    pl.action === "pending"
                                      ? "#94a3b8"
                                      : pl.paid_amount >= pl.claimed_amount
                                        ? "#16a34a"
                                        : "#0f172a",
                                  boxSizing: "border-box",
                                }}
                                step="0.01"
                                min="0"
                              />
                            </div>

                            {/* Action */}
                            <div
                              className="payment-progress-action"
                              data-label="Action"
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <ActionDropdown
                                value={pl.action}
                                onChange={(action) =>
                                  updateProgressLine(
                                    actualIdx,
                                    { action }
                                  )
                                }
                              />
                            </div>

                            {/* Agreement Made: inline input */}
                            {pl.action ===
                              "agreement_made" && (
                              <div
                                style={{
                                  gridColumn: "1 / -1",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginTop: 4,
                                  padding: "6px 8px",
                                  background: "#eff6ff",
                                  borderRadius: 8,
                                  fontSize: "0.72rem",
                                }}
                              >
                                <Handshake
                                  size={12}
                                  color="#2563eb"
                                />
                                <span
                                  style={{ color: "#2563eb" }}
                                >
                                  Agreed:
                                </span>
                                <span style={{ color: "#94a3b8" }}>$</span>
                                <input data-seq-private
                                  type="number"
                                  value={
                                    pl.agreed_amount ?? ""
                                  }
                                  onChange={(e) =>
                                    updateProgressLine(
                                      actualIdx,
                                      {
                                        agreed_amount:
                                          parseFloat(
                                            e.target.value
                                          ) || 0,
                                        paid_amount:
                                          parseFloat(
                                            e.target.value
                                          ) || 0,
                                        paid_percentage:
                                          pl.budget > 0
                                            ? Math.round(
                                                ((parseFloat(
                                                  e
                                                    .target
                                                    .value
                                                ) ||
                                                  0) /
                                                  pl.budget) *
                                                  100 *
                                                  100
                                              ) / 100
                                            : 0,
                                      }
                                    )
                                  }
                                  style={{
                                    width: 90,
                                    padding: "3px 6px",
                                    border:
                                      "1px solid #bfdbfe",
                                    borderRadius: 4,
                                    fontSize: "0.72rem",
                                    outline: "none",
                                  }}
                                  placeholder="Amount"
                                />
                              </div>
                            )}

                            {/* Works Not Completed: push info */}
                            {pl.action ===
                              "works_not_completed" && (
                              <div
                                style={{
                                  gridColumn: "1 / -1",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  marginTop: 4,
                                  padding: "5px 8px",
                                  background: "#fff7ed",
                                  borderRadius: 8,
                                  fontSize: "0.68rem",
                                  color: "#ea580c",
                                }}
                              >
                                <ArrowRight size={12} />
                                Will push {pl.claimed_percentage}% (
                                {formatCurrency(
                                  pl.claimed_amount
                                )}
                                ) to{" "}
                                <strong>
                                  {nextPeriodLabel}
                                </strong>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}

            {/* ── Section 3: Variations ── */}
            <div style={{ marginBottom: 24 }}>
                {/* Legacy warning banner (text only) */}
                {isLegacyReport && (
                  <div
                    style={{
                      padding: "8px 14px",
                      marginBottom: 8,
                      borderRadius: 10,
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: "0.72rem",
                      color: "#1e40af",
                    }}
                  >
                    <AlertTriangle size={14} style={{ flexShrink: 0, color: "#3b82f6" }} />
                    <span>
                      <span style={{ fontWeight: 600 }}>No linked variations</span>
                      <span style={{ color: "#3b82f6" }}>
                        {" "}(using date-based lookup). Use Link below to fix.
                      </span>
                    </span>
                  </div>
                )}
                {/* Link/Re-link button row */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                  <button
                    onClick={() => setShowLinkModal(true)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 10px",
                      borderRadius: 6,
                      border: "1px solid #e2e8f0",
                      background: "#f8fafc",
                      color: "#64748b",
                      fontSize: "0.68rem",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    <Link2 size={10} /> {isLegacyReport ? "Link" : "Re-link"}
                  </button>
                </div>
                {variationLines.length > 0 && (
                <>
                <button
                  type="button"
                  onClick={() =>
                    setVariationsOpen(!variationsOpen)
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "8px 0",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Layers size={14} color="#8b5cf6" />
                    <span
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Variations
                    </span>
                    <span
                      style={{
                        fontSize: "0.58rem",
                        fontWeight: 500,
                        color: "#8b5cf6",
                        background: "#f5f3ff",
                        padding: "1px 5px",
                        borderRadius: 4,
                      }}
                    >
                      {variations.length}
                    </span>
                  </div>
                  {variationsOpen ? (
                    <ChevronUp size={16} color="#64748b" />
                  ) : (
                    <ChevronDown size={16} color="#64748b" />
                  )}
                </button>

                {variationsOpen && (
                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      overflow: "hidden",
                    }}
                  >
                    {variationLines.map((vl, idx) => {
                      const actionColors = getActionColor(
                        vl.action
                      );
                      return (
                        <div
                          key={vl.variation_id}
                          style={{
                            padding: "10px 12px",
                            borderBottom:
                              idx <
                              variationLines.length - 1
                                ? "1px solid #f1f5f9"
                                : "none",
                            background:
                              vl.action !== "paid"
                                ? actionColors.bg
                                : "transparent",
                            transition:
                              "background 0.15s",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent:
                                "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  color: "#0f172a",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                {vl.variation_name}
                                {vl.is_outstanding && (
                                  <span
                                    style={{
                                      fontSize: "0.58rem",
                                      fontWeight: 700,
                                      textTransform: "uppercase",
                                      color: "#b45309",
                                      background: "rgba(245,158,11,0.12)",
                                      border: "1px solid rgba(245,158,11,0.3)",
                                      padding: "1px 5px",
                                      borderRadius: "3px",
                                    }}
                                    title="Raised in a prior month and still outstanding"
                                  >
                                    Outstanding
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.65rem",
                                  color: "#64748b",
                                  marginTop: 2,
                                }}
                              >
                                Claimed:{" "}
                                {formatCurrency(
                                  vl.claimed_amount
                                )}{" "}
                                • Paid:{" "}
                                <span data-seq-private=""
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      vl.action ===
                                      "voided"
                                        ? "#dc2626"
                                        : "#16a34a",
                                  }}
                                >
                                  {vl.action ===
                                  "voided"
                                    ? "$0"
                                    : formatCurrency(
                                        vl.paid_amount
                                  )}
                                </span>
                              </div>
                            </div>
                            <ActionDropdown
                              value={vl.action}
                              onChange={(action) =>
                                updateVariationLine(
                                  idx,
                                  { action }
                                )
                              }
                            />
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 8,
                              marginTop: 8,
                              fontSize: "0.72rem",
                            }}
                          >
                            <label style={{ color: "#64748b", fontWeight: 600 }}>
                              Amount Paid
                            </label>
                            <span style={{ color: "#94a3b8" }}>$</span>
                            <input data-seq-private
                              type="number"
                              value={
                                vl.action === "voided" ||
                                vl.action === "held" ||
                                vl.action === "works_not_completed" ||
                                vl.action === "pending"
                                  ? 0
                                  : vl.paid_amount
                              }
                              disabled={
                                vl.action === "voided" ||
                                vl.action === "held" ||
                                vl.action === "works_not_completed" ||
                                vl.action === "pending"
                              }
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const amt = raw === "" ? 0 : parseFloat(raw);
                                if (isNaN(amt)) return;
                                updateVariationLine(idx, {
                                  paid_amount: amt,
                                  paid_pct:
                                    vl.effective_amount > 0
                                      ? Math.round((amt / vl.effective_amount) * 100 * 100) / 100
                                      : 0,
                                  agreed_amount:
                                    vl.action === "agreement_made" ? amt : vl.agreed_amount,
                                });
                              }}
                              style={{
                                width: 110,
                                padding: "4px 6px",
                                border: `1px solid ${
                                  vl.action === "voided" ||
                                  vl.action === "held" ||
                                  vl.action === "works_not_completed" ||
                                  vl.action === "pending"
                                    ? "#e2e8f0"
                                    : "#d1d5db"
                                }`,
                                borderRadius: 5,
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                textAlign: "right",
                                outline: "none",
                                background:
                                  vl.action === "voided" ||
                                  vl.action === "held" ||
                                  vl.action === "works_not_completed" ||
                                  vl.action === "pending"
                                    ? "#f1f5f9"
                                    : "#fff",
                                color:
                                  vl.action === "voided" ||
                                  vl.action === "held" ||
                                  vl.action === "works_not_completed" ||
                                  vl.action === "pending"
                                    ? "#94a3b8"
                                    : vl.paid_amount >= vl.claimed_amount
                                      ? "#16a34a"
                                      : "#0f172a",
                                boxSizing: "border-box",
                              }}
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </>
                )}
            </div>

            {/* ── Section 4: Install & Hire ── */}
            {hireLines.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <button
                  type="button"
                  onClick={() => setHireOpen(!hireOpen)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "8px 0",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Wrench size={14} color="#0369a1" />
                    <span
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Install & Hire
                    </span>
                    <span
                      style={{
                        fontSize: "0.58rem",
                        fontWeight: 500,
                        color: "#0369a1",
                        background: "#f0f9ff",
                        padding: "1px 5px",
                        borderRadius: 4,
                      }}
                    >
                      {hireLines.length} items
                    </span>
                  </div>
                  {hireOpen ? (
                    <ChevronUp size={16} color="#64748b" />
                  ) : (
                    <ChevronDown size={16} color="#64748b" />
                  )}
                </button>

                {hireOpen && (
                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      overflow: "hidden",
                    }}
                  >
                    {hireLines.map((hl, idx) => {
                      const actionColors = getActionColor(hl.action);
                      const isZeroed =
                        hl.action === "voided" ||
                        hl.action === "held" ||
                        hl.action === "works_not_completed" ||
                        hl.action === "pending";
                      return (
                        <div
                          key={hl.hire_billing_id}
                          style={{
                            padding: "10px 12px",
                            borderBottom:
                              idx < hireLines.length - 1
                                ? "1px solid #f1f5f9"
                                : "none",
                            background:
                              hl.action !== "paid"
                                ? actionColors.bg
                                : "transparent",
                            transition: "background 0.15s",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 12,
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  color: "#0f172a",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  minWidth: 0,
                                }}
                              >
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: 170,
                                  }}
                                  title={`${hl.docket_no} ${hl.item_name}`}
                                >
                                  {hl.docket_no}
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: "0.68rem",
                                  color: "#64748b",
                                  marginTop: 2,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 260,
                                }}
                                title={hl.item_name}
                              >
                                {hl.item_name}
                              </div>
                              <div data-seq-private=""
                                style={{
                                  fontSize: "0.65rem",
                                  color: "#64748b",
                                  marginTop: 2,
                                }}
                              >
                                Claimed: {formatCurrency(hl.claimed_amount)}{" "}
                                • {hl.weeks_in_period.toFixed(2)} weeks • Paid:{" "}
                                <span data-seq-private=""
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      hl.action === "voided"
                                        ? "#dc2626"
                                        : "#16a34a",
                                  }}
                                >
                                  {isZeroed ? "$0" : formatCurrency(hl.paid_amount)}
                                </span>
                              </div>
                            </div>
                            <ActionDropdown
                              value={hl.action}
                              onChange={(action) =>
                                updateHireLine(idx, { action })
                              }
                            />
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 8,
                              marginTop: 8,
                              fontSize: "0.72rem",
                            }}
                          >
                            <label style={{ color: "#64748b", fontWeight: 600 }}>
                              Amount Paid
                            </label>
                            <span style={{ color: "#94a3b8" }}>$</span>
                            <input data-seq-private
                              type="number"
                              value={isZeroed ? 0 : hl.paid_amount}
                              disabled={isZeroed}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const amt = raw === "" ? 0 : parseFloat(raw);
                                if (isNaN(amt)) return;
                                updateHireLine(idx, {
                                  paid_amount: amt,
                                  paid_pct:
                                    hl.claimed_amount !== 0
                                      ? roundCurrency((amt / hl.claimed_amount) * 100)
                                      : 0,
                                  agreed_amount:
                                    hl.action === "agreement_made" ? amt : hl.agreed_amount,
                                });
                              }}
                              style={{
                                width: 110,
                                padding: "4px 6px",
                                border: `1px solid ${isZeroed ? "#e2e8f0" : "#d1d5db"}`,
                                borderRadius: 5,
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                textAlign: "right",
                                outline: "none",
                                background: isZeroed ? "#f1f5f9" : "#fff",
                                color:
                                  isZeroed
                                    ? "#94a3b8"
                                    : hl.paid_amount >= hl.claimed_amount
                                      ? "#16a34a"
                                      : "#0f172a",
                                boxSizing: "border-box",
                              }}
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Section 4: Retention Release ── */}
            {retentionClaimed > 0 && (
              <div style={{ marginBottom: 24 }}>
                <button
                  type="button"
                  onClick={() =>
                    setRetentionOpen(!retentionOpen)
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "8px 0",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Lock size={14} color="#d97706" />
                    <span
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Retention Release
                    </span>
                  </div>
                  {retentionOpen ? (
                    <ChevronUp size={16} color="#64748b" />
                  ) : (
                    <ChevronDown size={16} color="#64748b" />
                  )}
                </button>

                {retentionOpen && (
                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: "12px 14px",
                      background:
                        retentionLine.action !== "paid"
                          ? getActionColor(
                              retentionLine.action
                            ).bg
                          : "#fffbeb",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: "0.78rem",
                            color: "#0f172a",
                          }}
                        >
                          Retention Release
                        </div>
                        <div
                          style={{
                            fontSize: "0.68rem",
                            color: "#64748b",
                            marginTop: 2,
                          }}
                        >
                          Outstanding balance:{" "}
                          {formatCurrency(retentionClaimed, true)}{" "}
                          • Received:{" "}
                          <span data-seq-private=""
                            style={{
                              fontWeight: 600,
                              color:
                                retentionLine.action ===
                                "voided"
                                  ? "#dc2626"
                                  : "#16a34a",
                            }}
                          >
                            {retentionLine.action ===
                            "voided"
                              ? "$0"
                              : formatCurrency(
                                  retentionPaidTotal
                                )}
                          </span>
                        </div>
                        {currentReportRelease > 0 && (
                          <div data-seq-private=""
                            style={{
                              fontSize: "0.68rem",
                              color: "#92400e",
                              marginTop: 2,
                            }}
                          >
                            This report release line: {formatCurrency(currentReportRelease, true)}
                          </div>
                        )}
                      </div>
                      <ActionDropdown
                        value={retentionLine.action}
                        onChange={(action) => {
                          const updated = {
                            ...retentionLine,
                            action,
                          };
                          if (action === "voided" || action === "held") {
                            updated.paid_amount = 0;
                          } else if (action === "paid") {
                            // Auto-fill only when coming from zeroed state (allows overpayment)
                            if (retentionLine.paid_amount === 0 || retentionLine.action === "voided" || retentionLine.action === "held" || retentionLine.action === "works_not_completed" || retentionLine.action === "pending") {
                              updated.paid_amount =
                                retentionClaimed;
                            }
                          }
                          setRetentionLine(updated);
                        }}
                      />
                    </div>

                    {retentionLine.action ===
                      "agreement_made" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 8,
                          padding: "5px 8px",
                          background: "#eff6ff",
                          borderRadius: 8,
                          fontSize: "0.72rem",
                        }}
                      >
                        <Handshake
                          size={12}
                          color="#2563eb"
                        />
                        <span style={{ color: "#2563eb" }}>
                          Agreed: $
                        </span>
                        <input data-seq-private
                          type="number"
                          value={
                            retentionLine.agreed_amount ??
                            ""
                          }
                          onChange={(e) =>
                            setRetentionLine({
                              ...retentionLine,
                              agreed_amount:
                                Math.min(
                                  Math.max(0, parseFloat(e.target.value) || 0),
                                  retentionClaimed,
                                ),
                              paid_amount:
                                Math.min(
                                  Math.max(0, parseFloat(e.target.value) || 0),
                                  retentionClaimed,
                                ),
                            })
                          }
                          style={{
                            width: 100,
                            padding: "3px 6px",
                            border: "1px solid #bfdbfe",
                            borderRadius: 4,
                            fontSize: "0.72rem",
                            outline: "none",
                          }}
                          placeholder="Amount"
                        />
                      </div>
                    )}

                    {/* Retention amount input for paid action */}
                    {retentionLine.action === "paid" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 8,
                          fontSize: "0.72rem",
                        }}
                      >
                        <span style={{ color: "#64748b" }}>
                          Amount received: $
                        </span>
                        <input data-seq-private
                          type="number"
                          value={retentionLine.paid_amount}
                          onChange={(e) =>
                            setRetentionLine({
                              ...retentionLine,
                              paid_amount:
                                Math.min(
                                  Math.max(0, parseFloat(e.target.value) || 0),
                                  retentionClaimed,
                                ),
                            })
                          }
                          style={{
                            width: 100,
                            padding: "3px 6px",
                            border: "1px solid #e2e8f0",
                            borderRadius: 4,
                            fontSize: "0.72rem",
                            outline: "none",
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Section 5: Payment Summary ── */}
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                overflow: "hidden",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  padding: "10px 16px",
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Payment Summary
                </div>
              </div>

              <div style={{ padding: "12px 16px" }}>
                {/* Progress Paid */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.82rem",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ color: "#64748b" }}>
                    Progress Paid
                  </span>
                  <span data-seq-private=""
                    style={{
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    {formatCurrency(progressLinesPaidTotal, true)}
                  </span>
                </div>

                {/* Variations Reconciliation */}
                {variationLines.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.78rem",
                      marginBottom: 8,
                      color: "#94a3b8",
                    }}
                  >
                    <span>
                      Variations{" "}
                      <span style={{ fontSize: "0.65rem", fontStyle: "italic" }}>
                        ({variationLines.filter((vl) => vl.action !== "voided").length} of{" "}
                        {variationLines.length} included in claim)
                      </span>
                    </span>
                    <span data-seq-private="" style={{ fontWeight: 500 }}>
                      {formatCurrency(variationsPaidTotal, true)}
                    </span>
                  </div>
                )}

                {hireLines.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.78rem",
                      marginBottom: 8,
                      color: "#0369a1",
                    }}
                  >
                    <span>
                      Install & Hire{" "}
                      <span style={{ fontSize: "0.65rem", fontStyle: "italic", color: "#64748b" }}>
                        ({hireLines.filter((hl) => hl.action !== "voided").length} of{" "}
                        {hireLines.length} included in claim)
                      </span>
                    </span>
                    <span data-seq-private="" style={{ fontWeight: 600 }}>
                      {formatCurrency(hirePaidTotal, true)}
                    </span>
                  </div>
                )}

                {/* Divider before Total */}
                <div
                  style={{
                    height: 1,
                    background: "#e2e8f0",
                    margin: "8px 0",
                  }}
                />

                {/* Subtotal before retention */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.84rem",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: 600, color: "#0f172a" }}>
                    Subtotal before retention
                  </span>
                  <span data-seq-private="" style={{ fontWeight: 700, color: "#0f172a" }}>
                    {formatCurrency(subtotalExcGst, true)}
                  </span>
                </div>

                {/* Retention Held: with checkbox + % input */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "0.82rem",
                    marginBottom: 8,
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: retentionHeldEnabled ? "rgba(234, 179, 8, 0.06)" : "rgba(148, 163, 184, 0.06)",
                    border: retentionHeldEnabled ? "1px solid rgba(234, 179, 8, 0.15)" : "1px solid rgba(148, 163, 184, 0.15)",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: retentionHeldEnabled ? "#92400e" : "#94a3b8" }}>
                    <input
                      type="checkbox"
                      checked={retentionHeldEnabled}
                      onChange={(e) => setRetentionHeldEnabled(e.target.checked)}
                      style={{
                        width: 16,
                        height: 16,
                        cursor: "pointer",
                        accentColor: "#d97706",
                      }}
                    />
                    <span style={{ fontWeight: 600 }}>Retention Held</span>
                    {retentionHeldEnabled && (
                      <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <input data-seq-private
                          type="number"
                          value={retentionHeldPct}
                          onChange={(e) => setRetentionHeldPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                          style={{
                            width: 48,
                            padding: "2px 6px",
                            border: "1px solid #e2e8f0",
                            borderRadius: 4,
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            textAlign: "center",
                            background: "#fff",
                            color: "#92400e",
                          }}
                          step={0.5}
                          min={0}
                          max={100}
                        />
                        <span style={{ fontSize: "0.72rem", color: "#a16207" }}>%</span>
                      </span>
                    )}
                  </span>
                  <span data-seq-private=""
                    style={{
                      fontWeight: 600,
                      color: retentionHeldEnabled ? "#92400e" : "#94a3b8",
                    }}
                  >
                    {retentionHeldEnabled ? `-${formatCurrency(retentionHeldAmount, true)}` : formatCurrency(0, true)}
                  </span>
                </div>

                {/* Retention Release (only show if retention section has paid amounts) */}
                {retentionPaidTotal > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.78rem",
                      marginBottom: 8,
                      padding: "6px 10px",
                      borderRadius: 6,
                      background: "rgba(34, 197, 94, 0.06)",
                      border: "1px solid rgba(34, 197, 94, 0.15)",
                    }}
                  >
                    <span style={{ color: "#15803d", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}>
                      <Lock size={11} />
                      Retention Release
                      <span style={{ fontSize: "0.62rem", color: "#16a34a", fontWeight: 400, fontStyle: "italic" }}>
                        (claimed back)
                      </span>
                    </span>
                    <span data-seq-private="" style={{ fontWeight: 600, color: "#15803d" }}>
                      +{formatCurrency(retentionPaidTotal, true)}
                    </span>
                  </div>
                )}

                <div
                  style={{
                    height: 1,
                    background: "#e2e8f0",
                    margin: "10px 0 8px",
                  }}
                />

                {/* Taxable subtotal after retention adjustments */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.84rem",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: 700, color: "#0f172a" }}>
                    Taxable subtotal (Ex GST)
                  </span>
                  <span data-seq-private="" style={{ fontWeight: 800, color: "#0f172a" }}>
                    {formatCurrency(taxableSubtotalExcGst, true)}
                  </span>
                </div>

                {/* GST (10%) */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.82rem",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ color: "#64748b" }}>
                    GST (10%)
                  </span>
                  <span data-seq-private="" style={{ fontWeight: 600, color: "#0f172a" }}>
                    {formatCurrency(gstAmount, true)}
                  </span>
                </div>

                {/* Subtotal including GST */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.84rem",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: 700, color: "#0f172a" }}>
                    Subtotal (Inc GST)
                  </span>
                  <span data-seq-private="" style={{ fontWeight: 800, color: "#0f172a" }}>
                    {formatCurrency(subtotalIncGst, true)}
                  </span>
                </div>

                {/* Double divider before calculated/actual totals */}
                <div
                  style={{
                    height: 2,
                    background: "linear-gradient(to right, #cbd5e1, #94a3b8, #cbd5e1)",
                    margin: "10px 0",
                  }}
                />

                {/* Calculated Payment */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.92rem",
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Calculated Payment
                  </span>
                  <span data-seq-private=""
                    style={{
                      fontWeight: 800,
                      color:
                        calculatedPaymentAmount > 0
                          ? "#16a34a"
                          : "#94a3b8",
                    }}
                  >
                    {formatCurrency(calculatedPaymentAmount, true)}
                  </span>
                </div>

                {/* Amount Received */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    fontSize: "0.82rem",
                    marginBottom: 8,
                    padding: "8px 10px",
                    borderRadius: 6,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <label
                    htmlFor="amount-received"
                    style={{
                      color: "#334155",
                      fontWeight: 700,
                    }}
                  >
                    Amount Received
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span style={{ color: "#64748b", fontWeight: 700 }}>$</span>
                    <input data-seq-private
                      id="amount-received"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step={0.01}
                      value={amountReceivedInput}
                      onChange={(e) => setAmountReceivedInput(e.target.value)}
                      style={{
                        width: 128,
                        padding: "6px 8px",
                        border: "1px solid #cbd5e1",
                        borderRadius: 6,
                        background: "#fff",
                        color: "#0f172a",
                        fontSize: "0.84rem",
                        fontWeight: 700,
                        textAlign: "right",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>

                {/* Difference */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.82rem",
                  }}
                >
                  <span style={{ color: "#64748b" }}>
                    Difference
                  </span>
                  <span data-seq-private=""
                    style={{
                      fontWeight: 600,
                      color:
                        difference > 0
                          ? "#ef4444"
                          : difference < 0
                            ? "#16a34a"
                            : "#94a3b8",
                    }}
                  >
                    {difference > 0
                      ? `${formatCurrency(difference, true)} underpaid`
                      : difference < 0
                        ? `${formatCurrency(Math.abs(difference), true)} overpaid`
                        : formatCurrency(0, true)}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Section 6: Date Paid ── */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 6,
                }}
              >
                <Calendar size={14} color="#64748b" />
                <span
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#334155",
                  }}
                >
                  Date Paid
                </span>
              </div>
              <input
                type="date"
                className="form-input"
                value={datePaid}
                onChange={(e) => setDatePaid(e.target.value)}
                style={{
                  width: "100%",
                  fontSize: "0.82rem",
                  padding: "8px 12px",
                }}
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div
            style={{
              padding: "14px 24px",
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              background: "#f8fafc",
              flexShrink: 0,
            }}
          >
            <button
              className="btn btn-secondary"
              onClick={onClose}
              style={{
                padding: "9px 20px",
                fontSize: "0.82rem",
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !isDirty}
              style={{
                padding: "9px 24px",
                fontSize: "0.82rem",
                display: "flex",
                alignItems: "center",
                gap: 6,
                opacity: !isDirty ? 0.5 : 1,
                cursor: !isDirty
                  ? "not-allowed"
                  : "pointer",
              }}
            >
              {saving
                ? "Saving..."
                : !isDirty
                  ? "No Changes"
                  : "Save Payment"}
            </button>
          </div>
        </div>
      </div>

      {/* Link Variations Modal */}
      {showLinkModal && (
        <LinkVariationsModal
          reportId={report.id}
          claimNumber={report.claim_number}
          claimMonth={report.claim_month}
          claimYear={report.claim_year}
          projectId={report.project_id}
          reportVariationTotal={reportVariationTotal}
          currentLinkedIds={currentLinkedIds}
          onClose={() => setShowLinkModal(false)}
          onLinked={() => {
            setShowLinkModal(false);
            if (onRefresh) {
              onRefresh();
            } else {
              onSaved();
            }
          }}
        />
      )}

    </>
  );
}

// Re-export SubcategoryClaimInfo for use by parent component
export type { SubcategoryClaimInfo };
